export const SCROLL_LOCK_CLASS = 'overflow-hidden';

const NOTICE_SELECTOR = '#cookies-notice';
const TRIGGER_SELECTOR = '.js-cookie-trigger';

/**
 * Check if the visitor can still make a cookie choice
 *
 * @param notice {HTMLElement|null}
 * @returns {boolean}
 */
function isNoticeUsable(notice) {
  if (!notice || !notice.isConnected) return false;

  const styles = window.getComputedStyle(notice);

  if (styles.display === 'none' || styles.visibility === 'hidden') return false;

  // A notice without triggers leaves nothing to click
  return notice.querySelector(TRIGGER_SELECTOR) !== null;
}

/**
 * The scroll lock is rendered server side on the <html> element, so it outlives
 * a cookie notice that never makes it into the DOM. Browser extensions strip
 * cookie notices, which would leave the visitor on a page they cannot scroll
 * with no way left to make a choice. Release the lock whenever that happens.
 *
 * @returns {MutationObserver|null} The observer, or null when nothing is left to watch
 */
export default function guardScrollLock() {
  const html = document.documentElement;

  /**
   * @returns {boolean} True when the lock is gone and the guard is done
   */
  const release = () => {
    // Nothing to release, the visitor already made a choice
    if (!html.classList.contains(SCROLL_LOCK_CLASS)) return true;

    // Keep the lock while a choice can still be made
    if (isNoticeUsable(document.querySelector(NOTICE_SELECTOR))) return false;

    html.classList.remove(SCROLL_LOCK_CLASS);

    return true;
  };

  // The notice can already be gone before this script runs
  if (release()) return null;

  let observer = null;

  // ... or it can be stripped afterwards
  const onMutation = () => {
    if (release() && observer) observer.disconnect();
  };

  observer = new MutationObserver(onMutation);
  observer.observe(document.body, { childList: true });

  return observer;
}
